/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 23-09-2026
 *
 * De verdeling van het bouwscherm tussen het modelcanvas en het simulatiescherm: in welke richting de
 * scheiding staat en hoeveel ruimte de simulatie krijgt. Tot golf 4e zat dit als jQuery in de
 * AngularJS-hoofdcontroller (`setResizeEvents` en `updateWindowSplit`), met de css-waarden per richting
 * in `localStorage`.
 *
 * Hier staat alleen het getal. Wie het toepast is `dl-splitter` en het bouwscherm; de canvassen merken
 * een gewijzigde maat zelf op (de `ResizeObserver` in `DlCytoCanvasBaseDirective`), dus er hoort geen
 * melding bij.
 */
import {computed, Injectable, signal} from '@angular/core';
import {Sihwlog} from 'sihw-ngx';

/**
 * De richting waarin de scheiding loopt. `hori` betekent: naast elkaar, met een verticale
 * scheidingslijn. De andere stand is er sinds 19-09-2022 niet meer in de interface, maar de
 * berekeningen kennen hem nog.
 */
export type Splitrichting = 'hori' | 'verti';

/**
 * Hoeveel procent van de ruimte de simulatie standaard krijgt, per richting. Dezelfde getallen als de
 * ng1-defaults (`left: 80%` bij hori, `bottom: 70%` bij verti).
 */
const STANDAARD: Record<Splitrichting, number> = {hori: 20, verti: 30};

/**
 * Hoeveel procent de simulatie minstens en hoogstens krijgt. In ng1 was dat 80 px aan weerszijden; een
 * percentage werkt in beide richtingen en overleeft een venster dat van maat verandert.
 */
const MINIMUM = 8;
const MAXIMUM = 92;

/**
 * Waar de verdeling bewaard wordt. Een eigen sleutel: `splitwindow_hori` uit de AngularJS-tijd bewaart
 * twee complete css-objecten in een andere vorm, en staat nog in de browsers van gebruikers.
 */
const OPSLAGSLEUTEL = 'dl-splitsing';

@Injectable({providedIn: 'root'})
export class SchermindelingService {

    private readonly log = new Sihwlog('info', 'SchermindelingService');

    /**
     * De richting waarin de scheiding loopt.
     */
    private readonly _richting = signal<Splitrichting>('hori');

    /**
     * Hoeveel procent van de ruimte het simulatiescherm krijgt.
     */
    private readonly _simulatiedeel = signal(STANDAARD.hori);

    /**
     * De richting waarin de scheiding loopt; het simcanvas rekent zijn lay-out erop uit.
     */
    readonly richting = this._richting.asReadonly();

    /**
     * Hoeveel procent van de ruimte het simulatiescherm krijgt.
     */
    readonly simulatiedeel = this._simulatiedeel.asReadonly();

    /**
     * Hoeveel procent van de ruimte het modelcanvas krijgt.
     */
    readonly modeldeel = computed(() => 100 - this._simulatiedeel());

    constructor() {
        this._simulatiedeel.set(this.bewaarde() ?? STANDAARD[this._richting()]);
    }

    /**
     * Zet de verdeling; het getal is het percentage voor het simulatiescherm en wordt binnen de
     * grenzen gebracht. Bewaart de keuze, zodat hij een verversing overleeft.
     */
    zetSimulatiedeel(procent: number): void {
        const begrensd = Math.min(MAXIMUM, Math.max(MINIMUM, procent));
        this._simulatiedeel.set(begrensd);
        try {
            localStorage.setItem(`${OPSLAGSLEUTEL}_${this._richting()}`, String(Math.round(begrensd)));
        } catch (fout) {
            this.log.debug('de splitsing kon niet bewaard worden', fout);
        }
    }

    /**
     * De bewaarde verdeling voor de huidige richting, of `null` als er niets (geldigs) staat.
     */
    private bewaarde(): number | null {
        const waarde = Number(localStorage.getItem(`${OPSLAGSLEUTEL}_${this._richting()}`));
        return Number.isFinite(waarde) && waarde >= MINIMUM && waarde <= MAXIMUM ? waarde : null;
    }
}
